import { isDesktop, PUBLIC_WEB_URL, RETIRED_SERVERS } from "./desktop.ts";
/**
 * World sharing through the MC Planner server (Cloudflare Worker + R2):
 * the host uploads the world once (content-addressed blobs + pre-baked region maps),
 * guests only need the invite link.
 *
 * Game textures are never shared: Mojang's assets may not be redistributed. Guests read textures from their
 * own game jar, or plan on the pre-baked map colours only (see controller.joinInvite).
 */
import { type SourceInit, type WorldLayout, openSource } from "@mcp/world";
import { packMap, unpackMap } from "./mapPack.ts";
import type { RegionMapResult } from "../engine/protocol.ts";

export interface WorldManifest {
  room: string;
  name: string;
  version: string;
  dataVersion: number;
  host: string;
  updatedAt: number;
  files: { path: string; size: number; sha256: string }[];
  maps: { dim: string; rx: number; rz: number; size: number; sha256: string }[];
}

// ---------- server addressing ----------

/** Accepts https://…, wss://…, host:port … → normalised http(s) origin (no alias handling). */
function rawOrigin(server: string) {
  let s = server.trim();
  if (!/^[a-z]+:\/\//i.test(s)) s = (/^(localhost|127\.|192\.168\.|10\.)/.test(s) ? "http://" : "https://") + s;
  const u = new URL(s);
  const proto = u.protocol === "wss:" || u.protocol === "https:" ? "https:" : "http:";
  return `${proto}//${u.host}`;
}

const PUBLIC_ORIGIN = rawOrigin(PUBLIC_WEB_URL);
/** earlier public addresses (lib/desktop.ts) — old invite links and saved settings still name them */
const RETIRED = new Set(RETIRED_SERVERS.map(rawOrigin).filter((o) => o !== PUBLIC_ORIGIN));

/** Accepts https://…, wss://…, host:port … → normalised http(s) origin; a retired public address becomes the current one. */
export function serverOrigin(server: string) {
  const o = rawOrigin(server);
  return RETIRED.has(o) ? PUBLIC_ORIGIN : o;
}
export const isRetiredServer = (server: string) => { try { return RETIRED.has(rawOrigin(server)); } catch { return false; } };
export const serverHost = (server: string) => new URL(serverOrigin(server)).host;
export const serverProtocol = (server: string): "ws" | "wss" => (serverOrigin(server).startsWith("https:") ? "wss" : "ws");

const api = (server: string, room: string) => `${serverOrigin(server)}/api/worlds/${encodeURIComponent(room)}`;
const blobUrl = (server: string, room: string, sha: string) => `${api(server, room)}/blobs/${sha}`;

/** Request headers for a shared world's files: the room token of an account world (lib/collab.ts), none for legacy rooms. */
export type RoomHeaders = () => Promise<Record<string, string>>;

export async function fetchManifest(server: string, room: string, headers?: RoomHeaders): Promise<WorldManifest | null> {
  const r = await fetch(api(server, room), { cache: "no-store", headers: headers ? await headers() : undefined });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error((await r.json().catch(() => ({})) as { error?: string }).error ?? `Server answered ${r.status}`);
  const m = (await r.json()) as WorldManifest;
  m.maps ??= [];
  return m;
}

/**
 * World files for the guest's workers — each file is an immutable, browser-cacheable blob URL.
 * @param headers the room token of an account world (valid for a planning session; the workers get a copy)
 */
export function manifestSource(server: string, m: WorldManifest, headers?: Record<string, string>): SourceInit {
  return {
    kind: "urls",
    // "__assets/…" = textures an older app version uploaded; the server no longer serves them
    files: m.files.filter((f) => !f.path.startsWith("__assets/")).map((f) => ({ path: f.path, url: blobUrl(server, m.room, f.sha256) })),
    headers,
    cache: "default",
  };
}

/** true if the host shared pre-baked map colours for this dimension (guests without a jar see those) */
export function hasMaps(m: WorldManifest, dim?: string) {
  return m.maps.some((x) => !dim || x.dim === dim);
}

async function getBlob(server: string, room: string, sha: string, headers?: RoomHeaders) {
  const r = await fetch(blobUrl(server, room, sha), { headers: headers ? await headers() : undefined });
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  return new Uint8Array(await r.arrayBuffer());
}

/** Loaders for the host's pre-baked region maps of one dimension ("rx,rz" → fetch). */
export function prebakedMaps(server: string, m: WorldManifest, dim: string, headers?: RoomHeaders) {
  const out = new Map<string, () => Promise<RegionMapResult>>();
  for (const e of m.maps.filter((x) => x.dim === dim)) out.set(`${e.rx},${e.rz}`, async () => unpackMap(e.rx, e.rz, await getBlob(server, m.room, e.sha256, headers)));
  return out;
}

// ---------- region map packing (lib/mapPack.ts; also used by the website's demo world) ----------

export { packMap, unpackMap };

// ---------- invite links ----------

export interface Invite { server: string; room: string }

export function inviteLink(inv: Invite) {
  // the desktop app runs on tauri://localhost — guests get the public website instead
  const base = isDesktop() ? PUBLIC_WEB_URL : location.origin + location.pathname;
  return `${base}#join=${encodeURIComponent(inv.room)}&server=${encodeURIComponent(serverOrigin(inv.server))}`;
}

export function parseInvite(hash = location.hash): Invite | null {
  const p = new URLSearchParams(hash.replace(/^#/, ""));
  const room = p.get("join"), server = p.get("server");
  if (!room || !server) return null;
  try { serverOrigin(server); } catch { return null; }
  return { room, server: serverOrigin(server) };
}

export function clearInviteFromUrl() {
  history.replaceState(null, "", location.pathname + location.search);
}

// ---------- host upload ----------

async function sha256(b: Uint8Array) {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", b as BufferSource));
  return Array.from(d, (x) => x.toString(16).padStart(2, "0")).join("");
}

const keyName = (server: string, room: string) => `mcp-hostkey:${serverOrigin(server)}:${room}`;

/** The saved host key of a room. Keys saved under a retired public address (older versions) move to the current one. */
function readHostKey(server: string, room: string): string | null {
  const key = localStorage.getItem(keyName(server, room));
  if (key || serverOrigin(server) !== PUBLIC_ORIGIN) return key;
  for (const old of RETIRED) {
    const k = localStorage.getItem(`mcp-hostkey:${old}:${room}`);
    if (k) {
      localStorage.setItem(keyName(server, room), k);
      return k;
    }
  }
  return null;
}

/** Files a guest needs: level.dat, world data and every region file of every dimension. */
function worldFiles(paths: string[], layout: WorldLayout) {
  const regionDirs = layout.dimensions.map((d) => d.dir + "region/");
  return paths.filter((p) =>
    p.startsWith(layout.root) &&
    (p === layout.root + "level.dat" ||
      (p.startsWith(layout.root + "data/") && p.endsWith(".dat")) ||
      (p.endsWith(".mca") && regionDirs.some((d) => p.startsWith(d)))));
}

/**
 * Uploads the world files + pre-baked maps of an account world (owner/admin, lib/collab.ts roomHeaders), then
 * publishes the manifest. Only files the server doesn't have yet are sent; each upload counts against the owner's
 * storage, and the server refuses it when the plan's storage is full.
 */
export async function shareWorld(
  opts: {
    server: string; room: string; source: SourceInit; layout: WorldLayout;
    headers: RoomHeaders;
    info: { name: string; version: string; dataVersion: number; host: string };
    /** pre-baked region maps of the dimension the host has open */
    maps?: { dim: string; regions: RegionMapResult[] } | null;
  },
  /** `step` "upload" while files go up (frac = files done / all), "publish" for the last call */
  onProgress: (msg: string, frac: number, step: "upload" | "publish") => void,
): Promise<WorldManifest> {
  const { server, room } = opts;
  const base = api(server, room);
  const auth = await opts.headers();
  const previous = await fetchManifest(server, room, async () => auth).catch(() => null);
  const haveRes = await fetch(`${base}/have`, { headers: auth });
  if (!haveRes.ok) throw new Error((await haveRes.json().catch(() => ({}))).error ?? `Sharing failed (${haveRes.status})`);
  const have = new Set<string>(haveRes.ok ? (await haveRes.json()).shas : []);

  // collect everything to publish: [manifest entry, lazy bytes]
  type Item = { kind: "file"; path: string } | { kind: "map"; dim: string; r: RegionMapResult };
  const src = await openSource(opts.source);
  const items: Item[] = worldFiles(src.paths(), opts.layout).map((path) => ({ kind: "file", path }));
  for (const r of opts.maps?.regions ?? []) items.push({ kind: "map", dim: opts.maps!.dim, r });

  const files: WorldManifest["files"] = [];
  // keep maps of other dimensions shared earlier
  const maps: WorldManifest["maps"] = (previous?.maps ?? []).filter((m) => m.dim !== opts.maps?.dim && have.has(m.sha256));
  let uploaded = 0, bytesUp = 0;
  const queue = items.map((it, i) => ({ it, i }));
  let done = 0;
  const mb = (n: number) => (n < 10e6 ? (n / 1e6).toFixed(1) : String(Math.round(n / 1e6)));
  const status = () => `${done} of ${items.length} files · ${mb(bytesUp)} MB sent${done > uploaded ? ` · ${done - uploaded} already there` : ""}`;
  onProgress(status(), 0, "upload");
  const worker = async () => {
    for (;;) {
      const next = queue.shift();
      if (!next) return;
      const { it } = next;
      const bytes = it.kind === "file" ? await src.read(it.path) : await packMap(it.r);
      const h = await sha256(bytes);
      if (!have.has(h)) {
        const r = await fetch(`${base}/blobs/${h}`, { method: "PUT", headers: { ...auth, "content-type": "application/octet-stream" }, body: bytes as BodyInit });
        if (!r.ok) {
          const msg = (await r.json().catch(() => ({})) as { error?: string }).error;
          throw new Error(msg ?? `Upload failed (${r.status})`);
        }
        have.add(h);
        uploaded++;
        bytesUp += bytes.byteLength;
      }
      if (it.kind === "map") maps.push({ dim: it.dim, rx: it.r.rx, rz: it.r.rz, size: bytes.byteLength, sha256: h });
      else files.push({ path: it.path, size: bytes.byteLength, sha256: h });
      done++;
      onProgress(status(), done / items.length, "upload");
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);

  onProgress(`${items.length} files · ${mb(bytesUp)} MB sent · saving the list of files`, 0.99, "publish");
  const r = await fetch(`${base}/manifest`, {
    method: "POST",
    headers: { ...auth, "content-type": "application/json" },
    body: JSON.stringify({ ...opts.info, files, maps }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `Publishing failed (${r.status})`);
  return r.json();
}

/** The host key stored by "Share world" in app versions before accounts (only that host has it — proves a legacy claim). */
export function hostKeyFor(server: string, room: string): string | null {
  try { return readHostKey(server, room); } catch { return null; }
}

export function isHostOf(server: string, room: string) {
  try { return !!readHostKey(server, room); } catch { return false; }
}
